import React, { useContext, useCallback, useRef, useEffect } from 'react';
import { IEditorProps } from './Editor';
import { getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';
import { ApprovalChain } from './../ApprovalChain/ApprovalChain';
import { ApprovalLevelDto } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Approvals/ApprovalLevelDto';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { getByStates } from 'components/assignments/utils/AssignmentUtils';
import { State } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Assignments';
import { createLevelRule } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Approvals/ApprovalChainEntityService';
import { newCreateLevelRuleDto } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Approvals/CreateLevelRuleDto';
import { EditorLabel } from './EditorLabel';
import { useEditorId } from './hooks/useEditorId';

interface IApprovalChainEditorProps extends IEditorProps {}

/**
 * It is need to apply Assignee Editor changes, to remove items with Removed and Excluded states
 */
function applyChanges(approvalLevels: ApprovalLevelDto[]): ApprovalLevelDto[] {
  if (!approvalLevels) {
    return [];
  }
  return approvalLevels.map(item => ({
    ...item,
    ...{ Approvers: getByStates(item.Approvers || [], State.Added, State.Initial) },
  }));
}

/**
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function ApprovalChainEditor({ child, path }: IApprovalChainEditorProps) {
  const { onChange, onShowDialogue, addBeforeSaveCallback, editingContextId } = useContext(PropertyEditorContext);
  const levels = getEditorConfigValue<ApprovalLevelDto[]>(child, 'Levels');
  const allowRules = getEditorConfigValue<boolean>(child, 'AllowRules');
  const allowExpiration = getEditorConfigValue<boolean>(child, 'AllowExpiration');
  const [id] = useEditorId(path);

  const editedLevels = useRef(levels);

  useEffect(() => {
    addBeforeSaveCallback(path, async () => {
      return await onChange(applyChanges(editedLevels.current), path);
    });
  }, [addBeforeSaveCallback, onChange, path]);

  const handleChange = useCallback((levels: ApprovalLevelDto[]) => {
    editedLevels.current = levels;
  }, []);

  const handleTimeToApproveChange = useCallback(
    (_: any, level: ApprovalLevelDto) => {
      return new Promise<string | null>(resolve => {
        onShowDialogue({
          node: child,
          path,
          editingContextId: editingContextId,
          name: 'ChangeTimeToApprove',
          options: {
            value: level.TimeToApprove,
            onChange: (value: string | null) => {
              resolve(value);
            },
          },
        });
      });
    },
    [child, editingContextId, onShowDialogue, path]
  );

  const handleChangeRule = useCallback(
    (levelNumber: number, level: ApprovalLevelDto) => {
      return new Promise<string>(async resolve => {
        let ruleId = level.RuleId;
        if (!ruleId) {
          ruleId = await createLevelRule({
            dto: newCreateLevelRuleDto({
              EditingContext: editingContextId,
              Path: path,
              LevelId: level.ApprovalDataId,
              Level: levelNumber,
              FolderId: Decisions.Navigation.currentFolderId,
            }),
          });
        }
        onShowDialogue({
          node: child,
          path,
          editingContextId: editingContextId,
          name: 'EditRule',
          options: {
            ruleId,
          },
          onDone: () => {
            resolve(ruleId);
          },
        });
      });
    },
    [child, editingContextId, onShowDialogue, path]
  );

  return (
    <EditorLabel htmlFor={id} editor={child} path={path}>
      <div className="dpc-approval-chain-editor">
        <ApprovalChain
          levels={editedLevels.current ?? levels}
          allowRules={allowRules}
          allowExpiration={allowExpiration}
          onChange={handleChange}
          onEditExpiration={handleTimeToApproveChange}
          onEditRule={handleChangeRule}
        />
      </div>
    </EditorLabel>
  );
}
